import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ShieldStarIcon as ShieldStar, ShieldCheckIcon as ShieldCheck, CheckCircleIcon as CheckCircle, ArrowsLeftRightIcon as ArrowsLeftRight, GitForkIcon as GitFork, FlagIcon as Flag, ReceiptIcon as Receipt, ChatCircleDotsIcon as ChatCircleDots, ChatCircleTextIcon as ChatCircleText, MegaphoneIcon as Megaphone, ImageIcon as ImageIcon, TagIcon as Tag, GiftIcon as Gift, GraduationCapIcon as GraduationCap, DatabaseIcon as Database, StorefrontIcon as Storefront, WarningIcon as Warning, ArrowRightIcon as ArrowRight, ScalesIcon as Scales, RocketIcon as Rocket, NewspaperIcon as Newspaper, CompassIcon as Compass, SparkleIcon as Sparkle, ChartLineUpIcon as ChartLineUp, FilmStripIcon as FilmStrip, FilePdfIcon as FilePdf, CalculatorIcon as Calculator, FolderSimpleIcon as FolderSimple, TrashIcon as Trash, GitMergeIcon as GitMerge, TruckIcon as Truck, XIcon as X, BankIcon as Bank, ChartBarIcon as ChartBar, UsersThreeIcon as UsersThree, MagnifyingGlassIcon as MagnifyingGlass, PushPinIcon as PushPin, PushPinSlashIcon as PushPinSlash, BriefcaseIcon as Briefcase, TestTubeIcon as TestTube, FileTextIcon as FileText, NotebookIcon as Notebook, BookOpenIcon as BookOpen, GlobeHemisphereWestIcon as GlobeHemisphereWest, DeviceMobileIcon as DeviceMobile, StethoscopeIcon as Stethoscope } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { Icon, Indicium } from "@/components/luxury";

/**
 * Admin Console — single landing page for every operational tool.
 *
 * Three sections:
 *   1. Stat strip with last-7-day activity (signups, listings, paid sessions).
 *   2. "Needs your attention" tiles for any non-zero queue, ordered by urgency.
 *   3. Full grid of admin tools (with live queue counts where applicable).
 *
 * First-visit dual-role nudge: a one-time card explaining the gold mode
 * switcher in the nav. Dismissed in localStorage.
 */
const ADMIN_ONBOARD_KEY = "admin_console_onboarded_v1";
const ADMIN_PINNED_KEY = "admin_console_pinned_v1";

// Functional groupings. Order here = order on the page. "Pinned" is a
// virtual group (built from localStorage) and renders above all others.
const GROUPS = [
  { id: "operations",  label: "Operations",          subtitle: "Day-to-day queues and admin actions" },
  { id: "money",       label: "Money",               subtitle: "Revenue, pricing, payouts, fraud" },
  { id: "people",      label: "People",              subtitle: "Users, personas, onboarding" },
  { id: "content",     label: "Content & Marketing", subtitle: "Site copy, photos, marketing materials" },
  { id: "compliance",  label: "Data & Compliance",   subtitle: "Legal, security, breeds, data ops" },
  { id: "launch",      label: "Launch & Strategy",   subtitle: "Readiness, playbooks, roadmap" },
];

const TOOLS = [
  {
    to: "/admin/growth",
    label: "Growth & worldwide flags",
    icon: GlobeHemisphereWest,
    desc: "Launch toggles (worldwide buyers, US sellers, Stripe Tax, intl listings), waitlists, referrals, lead magnets.",
    accent: "#C9A227",
    group: "launch",
  },
  {
    to: "/admin/push-test",
    label: "Push notification test",
    icon: DeviceMobile,
    desc: "Check Apple/Firebase delivery configuration, see registered devices, and send yourself a live test push.",
    accent: "#0E0E0E",
    group: "operations",
  },
  {
    to: "/admin/duplicates",
    label: "Find duplicate dogs",
    icon: GitMerge,
    desc: "Detect dogs registered twice (same name + breed + DOB) and one-click merge with full reference re-linking.",
    accent: "#0E0E0E",
    group: "operations",
  },
  {
    to: "/admin/transporters",
    label: "Manage transporters",
    icon: Truck,
    desc: "Seed verified haulers, view the active roster, pause/suspend/activate listings.",
    accent: "#0E0E0E",
    group: "operations",
  },
  {
    to: "/admin/transport-disputes",
    label: "Transport disputes",
    icon: Truck,
    desc: "Mediate frozen-escrow disputes — release, refund, or split with one click.",
    accent: "#C1272D",
    group: "operations",
  },
  {
    to: "/admin/accounting",
    label: "Accounting",
    icon: Calculator,
    desc: "Monthly P&L — revenue by SKU, Stripe fees, refunds, manual expense entry, CSV export.",
    accent: "#C9A227",
    group: "money",
  },
  {
    to: "/admin/documents",
    label: "Business documents",
    icon: FolderSimple,
    desc: "Vault for contracts, financials, IP filings, compliance docs. Admin-only.",
    accent: "#0E0E0E",
    group: "compliance",
  },
  {
    to: "/admin/compliance",
    label: "Consent & audit ledger",
    icon: ShieldStar,
    desc: "Every cookie, Terms, Privacy, marketing, Buyer Pass, and community-guidelines acceptance — searchable + CSV export for legal & audit.",
    accent: "#0E0E0E",
    group: "compliance",
  },
  {
    to: "/admin/transfers",
    label: "Transfers",
    icon: ArrowsLeftRight,
    desc: "Approve or reject ownership transfers between buyers and sellers.",
    countKey: "pending_transfers",
    accent: "#C9A227",
    group: "operations",
  },
  {
    to: "/admin/contracts",
    label: "Sale contracts",
    icon: FileText,
    desc: "Per-puppy sales contracts uploaded by breeders. Compliance + dispute audit only — we're a venue, not a party.",
    accent: "#0E0E0E",
    group: "compliance",
  },
  {
    to: "/admin/approvals",
    label: "Pedigree submissions",
    icon: GitFork,
    desc: "Owner-submitted ancestor records waiting on your review.",
    countKey: "pending_pedigrees",
    accent: "#C9A227",
    group: "operations",
  },
  {
    to: "/admin/reports",
    label: "Trust & safety reports",
    icon: Flag,
    desc: "Flagged dogs, kennels, and listings. Dismiss, warn, or hide.",
    countKey: "open_reports",
    accent: "#C1272D",
    group: "operations",
  },
  {
    to: "/admin/dna-queue",
    label: "DNA verification queue",
    icon: TestTube,
    desc: "Paid $75 DNA-Verified pedigree submissions awaiting review.",
    countKey: "pending_dna_verifications",
    accent: "#C9A227",
    group: "operations",
  },
  {
    to: "/admin/escrow",
    label: "Escrow & sales",
    icon: Receipt,
    desc: "Held funds, disputed sales, refund decisions.",
    countKey: "disputed_escrow",
    accent: "#C1272D",
    group: "money",
  },
  {
    to: "/inbox",
    label: "Contact inbox",
    icon: ChatCircleDots,
    desc: "User-to-admin messages and broadcast composer.",
    countKey: "unread_contacts",
    accent: "#C9A227",
    group: "operations",
  },
  {
    to: "/admin/users",
    label: "User management",
    icon: UsersThree,
    desc: "Find any user, promote to admin, demote, suspend or reinstate. Use this to grant admin access to your personal account after registering.",
    accent: "#C9A227",
    group: "people",
  },
  {
    to: "/admin/security",
    label: "Security & compliance",
    icon: ShieldCheck,
    desc: "Read-only inventory of every security control. Hand this to your insurance broker, lawyer, or auditor — printable.",
    accent: "#0E0E0E",
    group: "compliance",
  },
  {
    to: "/admin/operator-binder",
    label: "Operator Binder",
    icon: FolderSimple,
    desc: "Single hub for everything you'd hand to a broker, bank, vendor, or investor — Business Plan PDF, Insurance Bundle, all legal pages, plus audience checklists for what to attach where.",
    accent: "#C9A227",
    group: "compliance",
    featured: true,
  },
  {
    to: "/admin/business-classification",
    label: "Business & insurance classification",
    icon: Briefcase,
    desc: "How to describe the business on insurance, banking, and vendor forms. NAICS codes, what coverage to ask for, and the categories to avoid. Printable.",
    accent: "#0E0E0E",
    group: "compliance",
  },
  {
    to: "/admin/data",
    label: "Data & ops",
    icon: Database,
    desc: "User search, role changes, suspensions, CSV exports.",
    accent: "#0E0E0E",
    group: "compliance",
  },
  {
    to: "/admin/ip-portfolio",
    label: "IP portfolio (for counsel)",
    icon: Scales,
    desc: "Compiled audit of every trademark- and copyright-protectable asset on the platform. Download PDF or Markdown to share with your IP attorney.",
    accent: "#C9A227",
    group: "compliance",
  },
  {
    to: "/admin/personas",
    label: "User intelligence",
    icon: ChartBar,
    desc: "Persona distribution + stuck-state callouts: who's onboarded, who's stalled, who's leaving revenue on the table.",
    accent: "#0E0E0E",
    group: "people",
  },
  {
    to: "/admin/pricing",
    label: "Pricing & fees",
    icon: Receipt,
    desc: "Every priced SKU, every percentage we take. Edit prices, soft-disable products, retune Transport / Dog Shop / Escrow cuts.",
    accent: "#C9A227",
    group: "money",
  },
  {
    to: "/admin/pricing-reference",
    label: "Pricing Reference",
    icon: BookOpen,
    desc: "The canonical 'why' behind every price: persona map, refund posture, recent change-log, and rationale for every fee. Live SKU editor handles amounts; this handles the why.",
    accent: "#0F8043",
    group: "money",
  },
  {
    to: "/admin/intl-ops",
    label: "Intl Marketplace Ops",
    icon: GlobeHemisphereWest,
    desc: "Single ops dashboard for the 5 intl Lead Marketplace personas + Kennel Verified Inquiry. Revenue, Pro Subs, credit balances, paid inquiries, response rates, performance-bond refunds.",
    accent: "#C9A227",
    group: "money",
  },
  {
    to: "/admin/users-ops",
    label: "Users Hub",
    icon: UsersThree,
    desc: "Member-health dashboard: growth, activation, gone-quiet accounts, moderation states, chat safety — every card jumps to the right tool.",
    accent: "#C1272D",
    group: "people",
  },
  {
    to: "/admin/intl-couriers",
    label: "International Couriers",
    icon: GlobeHemisphereWest,
    desc: "Courier roster & moderation: applications queue with approve/reject, full credentials & insurance info, lead credits, Pro subs, lead history, one-click suspend/reactivate.",
    accent: "#C9A227",
    group: "money",
  },
  {
    to: "/admin/intl-sellers",
    label: "International Sellers",
    icon: GlobeHemisphereWest,
    desc: "Direct-charge (non-US) sellers: Stripe health, signed payment-agreement evidence, sales volume, push/inbox notify, one-click suspend, and an issues worklist for international orders.",
    accent: "#C9A227",
    group: "money",
  },
  {
    to: "/admin/onboarding",
    label: "Setup-guide overrides",
    icon: CheckCircle,
    desc: "Tick or untick any user's onboarding steps — unstick mis-graded users, pre-complete steps for power users, silence the nag on test accounts.",
    accent: "#15803D",
    group: "people",
  },
  {
    to: "/admin/feature-matrix",
    label: "Feature matrix",
    icon: Sparkle,
    desc: "Every shipped feature grouped by persona (breeders, owners, pros, buyers). Copy as markdown or print to PDF — perfect for demos, investors, and store listings.",
    accent: "#B8860B",
    group: "content",
  },
  {
    to: "/admin/marketing/refresh",
    label: "Marketing refresh",
    icon: Sparkle,
    desc: "One-click AI-generated marketing pack: elevator pitch (3 lengths) + press release + tweet thread + LinkedIn + demo-video script + persona emails + investor one-pager. Grounded in your live stats.",
    accent: "#C9A227",
    group: "content",
  },
  {
    to: "/admin/pitch-kit",
    label: "Pitch kit",
    icon: ChatCircleText,
    desc: "Ready-made talking points for lawyers, tech people, insurance brokers, and casual intros. Copy any section in one tap or download the whole kit as a text file to bring to a meeting.",
    accent: "#C9A227",
    group: "content",
  },
  {
    to: "/admin/marketing",
    label: "Marketing kit",
    icon: Megaphone,
    desc: "Demo videos, scripts, captions, share-link manager.",
    accent: "#0E0E0E",
    group: "content",
  },
  {
    to: "/admin/feed-prompts",
    label: "Feed prompts",
    icon: ChatCircleText,
    desc: "Weekly Dog's Voice topics — edit the rotation, schedule event overrides, push to lock screens.",
    accent: "#8A6D00",
    group: "content",
  },
  {
    to: "/admin/social-bios",
    label: "Social media bios",
    icon: ChatCircleText,
    desc: "One-tap copy for every bio surface — IG, X, TikTok, LinkedIn, Facebook, press one-liner, long-form About, plus scoped hashtag bundles. Canonical copy so the brand voice never drifts between platforms.",
    accent: "#C9A227",
    group: "content",
  },
  {
    to: "/admin/checkr-setup",
    label: "Checkr setup playbook",
    icon: ShieldCheck,
    desc: "Exact step-by-step instructions to go from MOCKED Checkr (today) to LIVE: signup, package selection, API keys, webhook wiring, payment flow, FCRA compliance, and renewals. Copy-pasteable code blocks.",
    accent: "#0E0E0E",
    group: "operations",
  },
  {
    to: "/admin/stripe-tax-setup",
    label: "Stripe Tax reference",
    icon: Receipt,
    desc: "Per-persona answer to 'can Stripe collect and hold sales tax for me?' — flow-by-flow status, seller setup checklist (CDTFA permit + Stripe Tax origin address), CA marketplace-facilitator threshold, and the P1 escrow migration playbook.",
    accent: "#C1272D",
    group: "operations",
  },
  {
    to: "/admin/about-photos",
    label: "About photos",
    icon: ImageIcon,
    desc: "Manage hero rotation on the public homepage.",
    accent: "#0E0E0E",
    group: "content",
  },
  {
    to: "/admin/media",
    label: "Media & Press",
    icon: FilmStrip,
    desc: "Host promo videos, flyers (PDF), testimonials, how-tos, and press coverage. Each item gets a shareable /media/<slug> link so you can paste it into emails and texts instead of sending people to Instagram.",
    accent: "#C9A227",
    group: "content",
  },
  {
    to: "/admin/messaging",
    label: "Message Mediation",
    icon: ChatCircleDots,
    desc: "Read provider ↔ client conversations when a dispute or report is filed. Quarantine threads, remove rule-violating messages, and add audit-logged internal notes. Every access is logged.",
    accent: "#C1272D",
    group: "compliance",
  },
  {
    to: "/admin/journal",
    label: "Journal CMS",
    icon: Notebook,
    desc: "Write, save drafts, and publish editorial posts to /journal — Markdown body, live preview, no code deploy required.",
    accent: "#C9A227",
    group: "content",
  },
  {
    to: "/admin/puppy-school",
    label: "Puppy school drip",
    icon: Notebook,
    desc: "Author the day 1/3/5/7/14/30 lessons new buyers receive after homecoming — Markdown body, drafts allowed.",
    accent: "#C9A227",
    group: "content",
  },
  {
    to: "/admin/breeds",
    label: "Breeds catalog",
    icon: Tag,
    desc: "Edit purebred + exotic breed entries that show on /breeds.",
    accent: "#0E0E0E",
    group: "compliance",
  },
  {
    to: "/admin/seller-standards",
    label: "Seller standards",
    icon: ShieldStar,
    desc: "Publish a new version, toggle which breeds require explicit acknowledgment, revoke badges.",
    accent: "#0E0E0E",
    group: "compliance",
  },
  {
    to: "/admin/legal-bundle",
    label: "Legal & compliance bundle",
    icon: Scales,
    desc: "Download every legal/compliance/regulatory draft — or email the whole bundle to outside counsel.",
    accent: "#C9A227",
    group: "compliance",
  },
  {
    to: "/admin/launch-readiness",
    label: "Launch readiness",
    icon: Rocket,
    desc: "Programmatic green/yellow/red checklist of every launch blocker — Stripe, Resend, domain, lawyer signoff, default credentials, test data.",
    accent: "#C1272D",
    group: "launch",
  },
  {
    to: "/admin/promo-codes",
    label: "Promo codes",
    icon: Tag,
    desc: "Create, edit, scope, and audit promo codes.",
    accent: "#0E0E0E",
    group: "money",
  },
  {
    to: "/admin/comp-list",
    label: "Comp list",
    icon: Gift,
    desc: "Per-email courtesy discounts — comp yourself, co-owners, or partners across every paid surface.",
    accent: "#C9A227",
    group: "money",
  },
  {
    to: "/admin/providers",
    label: "Service providers",
    icon: GraduationCap,
    desc: "Curation queue for trainers, groomers, walkers, sitters. Approve to publish them to /find-pros.",
    accent: "#0E0E0E",
    group: "people",
  },
  {
    to: "/admin/press-kit",
    label: "Press kit",
    icon: Newspaper,
    desc: "Live-stats hub with elevator pitches, taglines, brand assets, and a one-click downloadable bundle for journalists, partners, and brokers.",
    accent: "#C9A227",
    group: "content",
  },
  {
    to: "/admin/stripe-radar",
    label: "Stripe Radar rules",
    icon: ShieldStar,
    desc: "Copy-paste fraud-prevention rules (3DS gates, country mismatch, velocity caps) ready to drop into Stripe → Radar → Rules.",
    accent: "#C1272D",
    group: "money",
  },
  {
    to: "/admin/stripe-pivot-playbook",
    label: "Stripe pivot playbook",
    icon: Bank,
    desc: "Contingency plan if Stripe restricts dog-sale processing. Three pivot options ranked by code-disruption, with a decision tree for when their reply lands.",
    accent: "#C9A227",
    group: "money",
  },
  {
    to: "/admin/armstrong",
    label: "Armstrong storefront",
    icon: Storefront,
    desc: "Manage dog-training subscriptions (Stripe payment links), the dog-food catalog & orders, and Printify merch sync. Single hub for the family business.",
    accent: "#0E0E0E",
    group: "operations",
  },
  {
    to: "/admin/roadmap",
    label: "Roadmap & ideas",
    icon: Compass,
    desc: "Curated post-launch backlog of trust, revenue, and defensibility ideas. Tap any card to mark it Open / Interested / Deferred / Done.",
    accent: "#0E0E0E",
    group: "launch",
  },
  {
    to: "/admin/paywall-audit",
    label: "Paywall audit",
    icon: ShieldCheck,
    desc: "One-screen map of every paywall gate + per-plan subscriber counts + drift signals. Built after the Janis Storefront re-charge bug. Refresh anytime — data is live.",
    accent: "#C9A227",
    group: "launch",
  },
  {
    to: "/admin/connect-diagnose",
    label: "Stripe Connect diagnose",
    icon: Stethoscope,
    desc: "Paste a seller's email and see EXACTLY what Stripe says is blocking their payouts — disabled_reason, requirements.errors, pending_verification, plus a one-line recommendation telling you what to tell the seller. Built after Janis's stuck-onboarding loop. Use whenever a seller has tried Connect 3+ times and is still blocked.",
    accent: "#C9A227",
    group: "launch",
  },
  {
    to: "/admin/announcements",
    label: "Announcements",
    icon: Megaphone,
    desc: "One-click launcher for every whats_new campaign — see audience counts live, click Send, get an inbox + email blast to your breeders. Idempotent: re-running only hits new users since the last fire.",
    accent: "#C9A227",
    group: "launch",
  },
  {
    to: "/admin/mobile-launch",
    label: "Launch on the App Stores",
    icon: DeviceMobile,
    desc: "Plain-English, free-path guide to ship Armstrong K9 Registry to iPhone + Android. No Mac, no developer, ~$124 first year. Checklists, progress tracking, and direct sign-up links for Apple, Google Play, GitHub, and Firebase.",
    accent: "#C9A227",
    group: "launch",
  },
  {
    to: "/admin/launch-pack",
    label: "Launch pack",
    icon: Sparkle,
    desc: "Business plan, insurance & legal dossier, SEO inventory, and the sample-tour video uploader — everything you'd hand to a lawyer, broker, investor, or journalist.",
    accent: "#0E0E0E",
    group: "launch",
  },
  {
    to: "/admin/launch-playbook",
    label: "Launch playbook & IP",
    icon: Rocket,
    desc: "Step-by-step go-to-market with checkable progress + candid trademark / patent / copyright audit so you know exactly what to file (and what's a waste).",
    accent: "#C1272D",
    group: "launch",
  },
  {
    to: "/admin/docs/service_providers_prd",
    label: "Service Providers PRD",
    icon: FileText,
    desc: "Draft spec for adding walker/sitter/groomer/trainer storefronts. Phased build plan, reuse map, 7 key decisions to approve before any code ships.",
    accent: "#C9A227",
    group: "launch",
  },
  {
    to: "/admin/dogs/reassign",
    label: "Bulk reassign dogs",
    icon: ArrowsLeftRight,
    desc: "Move dogs currently owned by the admin account onto a real user account. Audit-trail entries are written per move; related litters, contracts, and breeding listings reattach automatically.",
    accent: "#C1272D",
    group: "operations",
  },
  {
    to: "/admin/storefronts/health",
    label: "Storefront health",
    icon: Storefront,
    desc: "Platform-wide health for every active Dog Shop storefront — subscription, payouts, Printify, recent revenue, fulfillment errors. Read-only.",
    accent: "#0F8043",
    group: "operations",
  },
  {
    to: "/admin/armstrong/migrate",
    label: "Migrate Armstrong → seller storefront",
    icon: Storefront,
    desc: "One-shot tool: move the Armstrong family's food catalog off the admin/platform account onto a real Dog Shop seller storefront (Armstrong Supreme Team). Soft-archives originals.",
    accent: "#C1272D",
    group: "operations",
  },
  {
    to: "/admin/storefronts/reassign",
    label: "Bulk reassign storefronts",
    icon: Storefront,
    desc: "Move Dog Shop storefronts off the admin account onto a clean seller account (e.g. Supreme Team K9). Products and orders stay attached; audit log is written per move.",
    accent: "#C1272D",
    group: "operations",
  },
  {
    to: "/admin/analytics",
    label: "Product analytics",
    icon: ChartLineUp,
    desc: "Embed your PostHog dashboard inline, copy starter funnel/revenue/retention recipes, and browse the live event catalog the platform emits.",
    accent: "#0E0E0E",
    group: "people",
  },
];

export default function AdminConsole() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [showOnboard, setShowOnboard] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/admin/overview")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setErr(formatApiError(e)); });
    try {
      if (localStorage.getItem(ADMIN_ONBOARD_KEY) !== "1") setShowOnboard(true);
    } catch { /* private mode */ }
    return () => { cancelled = true; };
  }, []);

  const dismissOnboard = () => {
    try { localStorage.setItem(ADMIN_ONBOARD_KEY, "1"); } catch {}
    setShowOnboard(false);
  };

  const queues = data?.queues || {};
  const activity = data?.activity_7d || {};
  const urgent = TOOLS.filter((t) => t.countKey && (queues[t.countKey] || 0) > 0);

  // Search query + pinned set live alongside the tool list so we can
  // build the layout in one render: pinned first, then the filtered
  // grouped grid below.
  const [query, setQuery] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem("admin_console_collapsed") || "[]"));
    } catch {
      return new Set();
    }
  });
  const toggleGroup = (id) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      try { localStorage.setItem("admin_console_collapsed", JSON.stringify([...next])); } catch { /* private mode */ }
      return next;
    });
  };

  const [pinned, setPinned] = useState(() => {
    try {
      const raw = localStorage.getItem(ADMIN_PINNED_KEY);
      return new Set(JSON.parse(raw || "[]"));
    } catch { return new Set(); }
  });
  const togglePin = (to) => {
    setPinned((prev) => {
      const next = new Set(prev);
      if (next.has(to)) next.delete(to); else next.add(to);
      try { localStorage.setItem(ADMIN_PINNED_KEY, JSON.stringify([...next])); } catch {}
      return next;
    });
  };

  const visibleTools = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return TOOLS;
    return TOOLS.filter((t) =>
      t.label.toLowerCase().includes(q) ||
      (t.desc || "").toLowerCase().includes(q) ||
      (t.group || "").toLowerCase().includes(q),
    );
  }, [query]);

  const pinnedTools = useMemo(
    () => TOOLS.filter((t) => pinned.has(t.to)),
    [pinned],
  );

  // For each group, the filtered tools that belong in it. We render
  // groups in the GROUPS array's declared order so the layout never
  // shuffles.
  const groupedVisible = useMemo(() => {
    const map = new Map(GROUPS.map((g) => [g.id, []]));
    visibleTools.forEach((t) => {
      const bucket = map.get(t.group) || map.get("operations");
      bucket.push(t);
    });
    return map;
  }, [visibleTools]);

  return (
    <AppShell>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-3 flex items-center gap-2">
            <ShieldStar size={14} weight="fill" className="text-gold" />
            Admin · Console
          </p>
          <h1 className="font-spectral font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">Command center</h1>
          <p className="text-sm text-[#6B6B6B] mt-3 max-w-2xl leading-relaxed">
            Every operational tool in one place. Your personal kennel
            dashboard lives at <Link to="/dashboard" className="underline underline-offset-4 hover:text-ink">/dashboard</Link>.
          </p>
        </div>
        <Button asChild className="bg-ink hover:bg-crimson text-white rounded-full h-11 px-5">
          <Link to="/dashboard" data-testid="admin-jump-to-kennel">
            My kennel dashboard <ArrowRight size={14} weight="bold" className="ml-1.5" />
          </Link>
        </Button>
      </div>

      {showOnboard && (
        <div className="mb-6 rounded-2xl bg-[#0E0E0E] text-white p-5 flex items-start gap-4" data-testid="admin-onboard-card">
          <div className="h-10 w-10 rounded-full bg-[#C9A227]/20 text-gold flex items-center justify-center flex-shrink-0">
            <ShieldStar size={20} weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm mb-1">You're admin <span className="opacity-60">·</span> AND a kennel owner</p>
            <p className="text-[12px] text-white/70 leading-relaxed">
              Use the gold pill in the top-right of the nav to flip between
              <strong> Admin console</strong> (this page) and <strong>My kennel</strong> (your personal
              dogs, litters, inquiries, and listings). All operational tools
              live here, all your kennel work lives at /dashboard.
            </p>
          </div>
          <button
            onClick={dismissOnboard}
            data-testid="admin-onboard-dismiss"
            className="text-white/40 hover:text-white -mt-1 -mr-1 p-1"
            aria-label="Dismiss"
          >
            <X size={14} weight="bold" />
          </button>
        </div>
      )}

      {err && (
        <div className="bg-[#FCE9EA] border border-[#C1272D]/40 text-[#7A1014] rounded-xl p-4 text-sm mb-6">
          Could not load admin overview: {err}
        </div>
      )}

      {/* Activity strip — Mongo source of truth. The PostHog Pulse strip
          below shows the *same* metrics computed from PostHog events
          and should match within a few minutes (event ingestion lag).
          If they diverge persistently, check `payment_transactions.completed_at`
          stamping in `_finalize_paid_session`. */}
      <div className="relative bg-ink text-white rounded-2xl p-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4 overflow-hidden shadow-luxury-dark" data-testid="admin-activity-strip">
        <span aria-hidden="true" className="pointer-events-none absolute inset-0 luxury-grain mix-blend-overlay opacity-[0.06]" />
        <Stat label="Pending items" value={data ? data.pending_total : "—"} accent />
        <Stat label="Signups today" value={activity.new_users_today ?? "—"} />
        <Stat label="Signups · 7d" value={activity.new_users_7d ?? "—"} />
        <Stat label="New dogs · 7d" value={activity.new_dogs_7d ?? "—"} />
        <Stat label="Paid sessions · 7d" value={activity.paid_sessions_7d ?? "—"} />
        <Stat label="Revenue · 7d" value={activity.gross_revenue_7d_usd != null ? `$${Math.round(activity.gross_revenue_7d_usd)}` : "—"} />
      </div>

      <PostHogPulseStrip />

      <GoWorldwideStatusRow />

      {/* Urgent queues */}
      {urgent.length > 0 && (
        <section className="mb-10" data-testid="admin-urgent-section">
          <h2 className="font-spectral text-2xl tracking-tight mb-4 flex items-center gap-2">
            <Warning size={20} weight="fill" className="text-crimson" />
            Needs your attention
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {urgent.map((t) => (
              <ToolCard key={t.to} tool={t} count={queues[t.countKey]} urgent />
            ))}
          </div>
        </section>
      )}

      {/* Full tool grid — search + pinned + grouped by function */}
      <section data-testid="admin-tools-section">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
          <h2 className="font-spectral text-2xl tracking-tight">All tools</h2>
          <div className="relative w-full md:w-96">
            <MagnifyingGlass
              size={14} weight="bold"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/40 pointer-events-none"
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Find a tool… (e.g. promo, transfer, security)"
              data-testid="admin-tools-search"
              className="w-full h-10 pl-9 pr-9 rounded-full bg-white border border-[#EAEAEA] text-[13px] text-ink placeholder:text-ink/40 outline-none focus:border-gold/60 focus:ring-1 focus:ring-gold/30 transition-colors"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                aria-label="Clear search"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ink/40 hover:text-ink"
              >
                <X size={12} weight="bold" />
              </button>
            )}
          </div>
        </div>

        {!data && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 9 }).map((_, i) => (
              <Skeleton key={i} className="h-32 rounded-xl" />
            ))}
          </div>
        )}

        {data && (
          <>
            {/* Pinned row — only shown when not actively searching, so
                a search like "pricing" doesn't reorder results. */}
            {!query.trim() && pinnedTools.length > 0 && (
              <div className="mb-10" data-testid="admin-pinned-row">
                <h3 className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold mb-3 flex items-center gap-1.5">
                  <PushPin size={11} weight="fill" /> Pinned
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {pinnedTools.map((t) => (
                    <ToolCard
                      key={t.to}
                      tool={t}
                      count={t.countKey ? queues[t.countKey] : null}
                      pinned
                      onTogglePin={() => togglePin(t.to)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Grouped grid — empty groups are hidden when filtering;
                each group is collapsible and the choice persists. */}
            {GROUPS.map((g) => {
              const items = groupedVisible.get(g.id) || [];
              if (items.length === 0) return null;
              const collapsed = collapsedGroups.has(g.id) && !query.trim();
              return (
                <div key={g.id} className="mb-10" data-testid={`admin-group-${g.id}`}>
                  <button
                    type="button"
                    onClick={() => toggleGroup(g.id)}
                    className="mb-3 text-left w-full group/hd"
                    data-testid={`admin-group-toggle-${g.id}`}
                  >
                    <h3 className="font-spectral text-xl tracking-tight text-ink flex items-center gap-2">
                      {g.label}
                      <span className="text-[11px] font-sans text-ink/40 tracking-normal">
                        {items.length}
                      </span>
                      <span className="text-[10px] font-sans text-ink/35 tracking-normal opacity-0 group-hover/hd:opacity-100 transition-opacity">
                        {collapsed ? "expand" : "collapse"}
                      </span>
                    </h3>
                    <p className="text-[12px] text-ink/55 mt-0.5">{g.subtitle}</p>
                  </button>
                  {!collapsed && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map((t) => (
                      <ToolCard
                        key={t.to}
                        tool={t}
                        count={t.countKey ? queues[t.countKey] : null}
                        pinned={pinned.has(t.to)}
                        onTogglePin={() => togglePin(t.to)}
                      />
                    ))}
                  </div>
                  )}
                </div>
              );
            })}

            {/* Empty-state for unmatched search */}
            {query.trim() && visibleTools.length === 0 && (
              <div className="rounded-2xl border border-dashed border-[#EAEAEA] bg-white p-8 text-center">
                <p className="text-sm text-ink/70">
                  No admin tools match <strong>"{query}"</strong>.
                </p>
                <Button
                  size="sm" variant="outline"
                  onClick={() => setQuery("")}
                  className="rounded-full mt-3"
                >
                  Clear search
                </Button>
              </div>
            )}
          </>
        )}
      </section>

      <SalesToolkitStrip />

      <DangerZoneCard />
    </AppShell>
  );
}

function Stat({ label, value, accent = false }) {
  return (
    <div className="text-center relative">
      <p className={`font-spectral font-light text-3xl tracking-tight font-tabular leading-none ${accent ? "bg-gold-gradient bg-clip-text text-transparent" : "text-white"}`}>
        {value}
      </p>
      <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/55 mt-2">{label}</p>
    </div>
  );
}

function ToolCard({ tool, count, urgent = false, pinned = false, onTogglePin }) {
  const ToolIcon = tool.icon;
  // Map the tile's accent hex back to the platform tone tokens. With
  // the rainbow killed in Pass 7 every accent is now ink/gold/crimson —
  // so this map is exhaustive and any future drift is caught by the
  // Icon primitive's dev warning.
  const ACCENT_TO_TONE = {
    "#0E0E0E": "ink",
    "#C9A227": "gold",
    "#C1272D": "crimson",
  };
  const tone = ACCENT_TO_TONE[tool.accent] || "ink";
  return (
    <div className="relative group">
      <Link
        to={tool.to}
        data-testid={`admin-tool-${tool.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
        className={`block bg-white border rounded-2xl p-5 hover:-translate-y-0.5 hover:shadow-gold-glow shadow-luxury-light transition-all duration-500 relative overflow-hidden ${
          urgent ? "border-crimson/40" : "border-[#EAEAEA] hover:border-gold/55"
        }`}
      >
        <div className="flex items-start gap-3">
          <Indicium
            icon={ToolIcon}
            tone={tone}
            size="md"
            className="transition-transform duration-500 group-hover:scale-105"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-semibold text-[15px] text-ink tracking-tight pr-7">{tool.label}</h3>
              {count != null && count > 0 && (
                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 font-tabular ${
                  urgent ? "bg-crimson text-white" : "bg-gold/15 text-[#8A6A00]"
                }`}>
                  {count}
                </span>
              )}
            </div>
            <p className="text-[12px] text-[#6B6B6B] mt-1 leading-relaxed">
              {tool.desc}
            </p>
          </div>
        </div>
        <div className="gold-hairline mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        <Icon as={ArrowRight} tone="muted" size="xs" weight="bold" className="absolute bottom-4 right-4 group-hover:text-gold transition-colors" />
      </Link>
      {/* Pin toggle — absolute, layered above the Link so click doesn't
          navigate. Visible always when pinned, on hover otherwise. */}
      {onTogglePin && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onTogglePin(); }}
          aria-label={pinned ? "Unpin" : "Pin to top"}
          data-testid={`admin-tool-pin-${tool.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={`absolute top-3 right-3 p-1.5 rounded-full transition-all ${
            pinned
              ? "text-[#8A6A00] bg-gold/15 hover:bg-gold/25 opacity-100"
              : "text-ink/35 hover:text-[#8A6A00] hover:bg-gold/10 opacity-0 group-hover:opacity-100 focus:opacity-100"
          }`}
        >
          {pinned
            ? <PushPin size={12} weight="fill" />
            : <PushPinSlash size={12} weight="bold" />}
        </button>
      )}
    </div>
  );
}

/**
 * GoWorldwideStatusRow — live state of the four worldwide-launch toggles
 * + international waitlist, pinned right above the urgent queues so the
 * launch posture is always visible. Replaces the pre-launch readiness
 * banner (full checks remain at /admin/launch-readiness).
 */
function GoWorldwideStatusRow() {
  const [policy, setPolicy] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/admin/platform/region-policy")
      .then((r) => { if (alive) setPolicy(r.data); })
      .catch(() => { if (alive) setPolicy(null); });
    return () => { alive = false; };
  }, []);
  if (!policy) return null;
  const chips = [
    { label: "Worldwide buyers", on: !policy.us_only, goodWhenOn: true },
    { label: "US sellers only", on: policy.sellers_us_only, goodWhenOn: true },
    { label: "Stripe Tax", on: policy.stripe_tax_all, goodWhenOn: true },
    { label: "Intl listings", on: policy.intl_listings, goodWhenOn: true },
  ];
  return (
    <Link
      to="/admin/growth"
      data-testid="admin-worldwide-status-row"
      className="group rounded-2xl border border-[#EAEAEA] bg-white p-4 mb-6 flex items-center gap-4 flex-wrap hover:border-gold/50 transition-colors"
    >
      <div className="flex items-center gap-2 flex-shrink-0">
        <div className="h-9 w-9 rounded-full bg-ink flex items-center justify-center">
          <GlobeHemisphereWest size={18} weight="duotone" className="text-gold" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#8A6A00]">Go worldwide</p>
          <p className="text-[12px] text-ink/60">
            {policy.international_waitlist_count} on intl waitlist
            {policy.international_waitlist_unnotified > 0 && ` · ${policy.international_waitlist_unnotified} not yet notified`}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap flex-1">
        {chips.map((c) => (
          <span
            key={c.label}
            data-testid={`worldwide-chip-${c.label.toLowerCase().replace(/ /g, "-")}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold ${
              c.on ? "bg-ink text-gold" : "bg-[#F2F2F2] text-[#888]"
            }`}
          >
            {c.label} · {c.on ? "ON" : "OFF"}
          </span>
        ))}
      </div>
      <span className="text-[12px] text-ink/50 inline-flex items-center gap-1 flex-shrink-0">
        Manage <ArrowRight size={12} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
      </span>
    </Link>
  );
}

/**
 * DangerZoneCard — preview-environment-only access to the demo-data
 * reset. Hidden in production (armstrongk9registry.com): a one-click
 * irreversible wipe has no business on a live registry's front page.
 */
function DangerZoneCard() {
  const isPreviewHost = /preview\.emergentagent\.com|emergentcf|localhost|127\.0\.0\.1/.test(
    window.location.hostname
  );
  if (!isPreviewHost) return null;
  return (
    <section className="mt-10" data-testid="admin-danger-zone-card">
      <Link
        to="/admin/reset"
        className="group flex items-center gap-3 rounded-2xl border-2 border-dashed border-crimson/40 bg-crimson/5 p-4 hover:border-crimson transition-colors"
      >
        <Trash size={18} weight="duotone" className="text-crimson flex-shrink-0" />
        <div className="flex-1">
          <p className="font-spectral text-base tracking-tight text-crimson">Reset demo data (preview only)</p>
          <p className="text-[12px] text-ink/60">
            Wipes dogs, kennels, litters &amp; payments. This card never appears on production.
          </p>
        </div>
        <ArrowRight size={14} weight="bold" className="text-crimson/70 group-hover:translate-x-0.5 transition-transform" />
      </Link>
    </section>
  );
}


/**
 * PostHogPulseStrip — last-7-day live KPI bar pulled directly from PostHog
 * via HogQL. Falls back to a "Configure PostHog" empty state when the
 * personal API key is not set.
 *
 * Auto-refreshes every 5 minutes (PostHog caches HogQL results, so this
 * is cheap). Click anywhere on the strip to drill down at /admin/analytics.
 */
function PostHogPulseStrip() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = () => {
      api.get("/admin/analytics/pulse")
        .then((r) => { if (alive) { setData(r.data); setErr(false); } })
        .catch(() => { if (alive) setErr(true); });
    };
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Render even when PostHog isn't configured — finance numbers now
  // come from Mongo and are always present, only the engagement metrics
  // (signups + D1 retention) require PostHog.
  if (err && !data) return null;

  const fmtInt = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString());
  const fmtUsd = (v) =>
    v === null || v === undefined
      ? "—"
      : Number(v).toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
          // `style: "currency"` defaults min to 2; setting max:0
          // alone triggers RangeError. Pin both.
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        });
  const fmtPct = (v) =>
    v === null || v === undefined ? "—" : `${Number(v).toFixed(1)}%`;

  return (
    <Link
      to="/admin/analytics"
      data-testid="admin-posthog-pulse"
      className="group block rounded-2xl border border-[#3F4A8A]/30 bg-gradient-to-br from-[#3F4A8A]/8 to-[#C9A227]/8 p-5 mb-8 hover:border-[#3F4A8A]/60 transition-colors"
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-[#0F8043] animate-pulse" />
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#3F4A8A]">
            Live · Last 7 days
          </p>
        </div>
        <ArrowRight
          size={14}
          weight="bold"
          className="text-[#3F4A8A] group-hover:translate-x-0.5 transition-transform"
        />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <PulseStat label="Signups" value={data ? fmtInt(data.signups_7d) : "…"} />
        <PulseStat label="Paid sessions" value={data ? fmtInt(data.paid_sessions_7d) : "…"} />
        <PulseStat label="Gross revenue" value={data ? fmtUsd(data.gross_revenue_7d_usd) : "…"} />
        <PulseStat label="D1 retention" value={data ? fmtPct(data.retention_d1_pct) : "…"} />
      </div>
    </Link>
  );
}

function PulseStat({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6B6B6B] mb-1">
        {label}
      </p>
      <p className="font-spectral text-2xl tracking-tight text-ink tabular-nums">
        {value}
      </p>
    </div>
  );
}


/**
 * Sales toolkit strip — shareable assets the admin downloads to send
 * to prospects (PDF pricing menu today; reels, brochures, etc. will
 * slot in here later). Each tile is a single download action, not a
 * page navigation — clicking fires a backend render and a browser
 * "Save as…" prompt.
 *
 * The pricing PDF is regenerated on every click so it always reflects
 * the live PACKAGES dict at the time of download. No caching, no
 * "is this version stale?" trust problem.
 */
function SalesToolkitStrip() {
  const [downloadingPricing, setDownloadingPricing] = useState(false);
  const [downloadingTransporterPitch, setDownloadingTransporterPitch] = useState(false);
  const [downloadingTransporterFaq, setDownloadingTransporterFaq] = useState(false);
  const [downloadingTransporterAgreement, setDownloadingTransporterAgreement] = useState(false);

  // Single download helper — every Sales Toolkit PDF goes through this
  // so we keep one place for blob handling, filename synthesis, and
  // error toasts. Endpoints differ only in path + filename prefix.
  const downloadPdf = async (path, filenamePrefix, successDesc, setBusy) => {
    setBusy(true);
    try {
      const res = await api.get(path, { responseType: "blob", timeout: 60_000 });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.download = `${filenamePrefix}-${ts}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Downloaded", { description: successDesc });
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't generate PDF.");
    } finally {
      setBusy(false);
    }
  };

  const downloadPricingMenu = () =>
    downloadPdf(
      "/admin/pricing-menu.pdf",
      "armstrong-k9-pricing",
      "Brand-aligned PDF, regenerated against live prices.",
      setDownloadingPricing,
    );

  const downloadTransporterPitch = () =>
    downloadPdf(
      "/admin/transporter-pitch.pdf",
      "armstrong-k9-transporter-pitch",
      "Shareable recruitment pitch — forward to any licensed hauler.",
      setDownloadingTransporterPitch,
    );

  const downloadTransporterFaq = () =>
    downloadPdf(
      "/admin/transporter-faq.pdf",
      "armstrong-k9-transporter-faq",
      "Six concrete answers — disputes, escrow, schedule, onboarding.",
      setDownloadingTransporterFaq,
    );

  const downloadTransporterAgreement = () =>
    downloadPdf(
      "/admin/transporter-agreement.pdf",
      "armstrong-k9-transporter-agreement",
      "Articles 1-11. Get this reviewed by your attorney before using in production.",
      setDownloadingTransporterAgreement,
    );

  return (
    <details
      className="mt-10 rounded-2xl border border-[#E5E5E5] bg-white"
      data-testid="admin-sales-toolkit-strip"
    >
      <summary className="cursor-pointer select-none p-5 flex items-baseline justify-between flex-wrap gap-2 list-none [&::-webkit-details-marker]:hidden">
        <h2 className="font-spectral text-xl tracking-tight flex items-center gap-2">
          <Indicium icon={FilePdf} tone="gold" size="sm" />
          Sales &amp; recruitment toolkit
        </h2>
        <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#6B6B6B]">
          PDFs to share with prospects — click to expand
        </span>
      </summary>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 p-5 pt-0">
        {/* Pricing menu PDF */}
        <div className="border border-[#EAEAEA] rounded-xl p-4 flex items-start gap-3" data-testid="admin-pricing-menu-card">
          <span
            className="flex items-center justify-center rounded-lg flex-shrink-0"
            style={{
              width: 44,
              height: 44,
              background: "#FBF1D8",
              border: "1px solid rgba(201,162,39,0.55)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), 0 2px 8px rgba(201,162,39,0.18)",
            }}
          >
            <FilePdf size={22} weight="duotone" className="text-gold-dark" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-spectral text-lg tracking-tight">Pricing menu</p>
            <p className="text-[12px] text-[#6B6B6B] mt-0.5 leading-relaxed">
              One-page heritage PDF covering every plan, fee, and add-on.
              Regenerated against live prices on every click.
            </p>
            <Button
              size="sm"
              onClick={downloadPricingMenu}
              disabled={downloadingPricing}
              className="mt-3 rounded-full bg-ink hover:bg-crimson text-white h-8 px-4 text-[12px]"
              data-testid="admin-pricing-menu-download"
            >
              {downloadingPricing ? "Generating…" : "Download PDF"}
            </Button>
          </div>
        </div>

        {/* Transporter recruitment pitch PDF */}
        <div
          className="border border-[#EAEAEA] rounded-xl p-4 flex items-start gap-3"
          data-testid="admin-transporter-pitch-card"
        >
          <span
            className="flex items-center justify-center rounded-lg flex-shrink-0"
            style={{
              width: 44,
              height: 44,
              background: "#EAF1F9",
              border: "1px solid rgba(31,86,153,0.45)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), 0 2px 8px rgba(31,86,153,0.18)",
            }}
          >
            <Truck size={22} weight="duotone" className="text-ink" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-spectral text-lg tracking-tight">Transporter pitch</p>
            <p className="text-[12px] text-[#6B6B6B] mt-0.5 leading-relaxed">
              Shareable recruitment one-pager for licensed dog and puppy
              haulers — pre-launch directory, 10% platform fee, escrowed payouts.
            </p>
            <Button
              size="sm"
              onClick={downloadTransporterPitch}
              disabled={downloadingTransporterPitch}
              className="mt-3 rounded-full bg-ink hover:bg-crimson text-white h-8 px-4 text-[12px]"
              data-testid="admin-transporter-pitch-download"
            >
              {downloadingTransporterPitch ? "Generating…" : "Download PDF"}
            </Button>
          </div>
        </div>

        {/* Transporter FAQ PDF */}
        <div
          className="border border-[#EAEAEA] rounded-xl p-4 flex items-start gap-3"
          data-testid="admin-transporter-faq-card"
        >
          <span
            className="flex items-center justify-center rounded-lg flex-shrink-0"
            style={{
              width: 44,
              height: 44,
              background: "#EAF1F9",
              border: "1px solid rgba(31,86,153,0.45)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), 0 2px 8px rgba(31,86,153,0.18)",
            }}
          >
            <Truck size={22} weight="duotone" className="text-ink" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-spectral text-lg tracking-tight">Transporter FAQ</p>
            <p className="text-[12px] text-[#6B6B6B] mt-0.5 leading-relaxed">
              Six honest answers on disputes, escrow protections,
              schedule control, repeat business, communication, onboarding.
            </p>
            <Button
              size="sm"
              onClick={downloadTransporterFaq}
              disabled={downloadingTransporterFaq}
              className="mt-3 rounded-full bg-ink hover:bg-crimson text-white h-8 px-4 text-[12px]"
              data-testid="admin-transporter-faq-download"
            >
              {downloadingTransporterFaq ? "Generating…" : "Download PDF"}
            </Button>
          </div>
        </div>

        {/* Transporter Agreement PDF */}
        <div
          className="border border-[#EAEAEA] rounded-xl p-4 flex items-start gap-3"
          data-testid="admin-transporter-agreement-card"
        >
          <span
            className="flex items-center justify-center rounded-lg flex-shrink-0"
            style={{
              width: 44,
              height: 44,
              background: "#FDECEC",
              border: "1px solid rgba(193,39,45,0.45)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), 0 2px 8px rgba(193,39,45,0.18)",
            }}
          >
            <Scales size={22} weight="duotone" className="text-crimson" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-spectral text-lg tracking-tight">Transporter Agreement v1.0</p>
            <p className="text-[12px] text-[#6B6B6B] mt-0.5 leading-relaxed">
              Articles 1-11 — relationship, eligibility, fee, escrow,
              disputes, indemnification. <span className="font-semibold text-crimson">Have your attorney review before production use.</span>
            </p>
            <Button
              size="sm"
              onClick={downloadTransporterAgreement}
              disabled={downloadingTransporterAgreement}
              className="mt-3 rounded-full bg-ink hover:bg-crimson text-white h-8 px-4 text-[12px]"
              data-testid="admin-transporter-agreement-download"
            >
              {downloadingTransporterAgreement ? "Generating…" : "Download PDF"}
            </Button>
          </div>
        </div>
      </div>
    </details>
  );
}
